/**
 * @file OnboardingWizard.js
 * @description Represents the onboarding wizard component for continuing the onboarding process.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import SetUserCredentialsForm from './SetUserCredentialsForm';
import SetCommunicationPreferencesForm from './SetCommunicationPreferencesForm';
import SetNotificationPreferencesForm from './SetNotificationPreferencesForm';
import EULAPage from './EULAPage';
import styles from './OnboardingPage.module.scss';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { useCompanyConfig } from '../../hooks/useCompanyConfig';
import { useTheme } from '../../store/ThemeContext';
import { getOnboardingDetails } from '../../services/userService';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import HeightScaledContent from '../ScaledContent/HeightScaledContent';
import PlaceholderLogo from '../ui/PlaceholderLogo';
import { UserType } from '../../utils/data/enums';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';

const OnboardingWizard = () => {
    const { onboardingCode } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const themeStyles = useTheme();
    const { isCompanyCodeReady, authData } = useCompanyConfig();
    const { companyConfig = null } = authData;

    const [notificationPreferences, setNotificationPreferences] = useState({});

    const [onboardingStage, setOnboardingStage] = useState('loading');
    const [onboardingError, setOnboardingError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [onboardingInfo, setOnboardingInfo] = useState({
        companyCode: '',
        onboardingCode: onboardingCode,
        onboarding_link: '',
        isSelfRegistration: false,
        profileData: null,
        userDetails: {},
        userCredentials: {},
        communicationPreferences: {},
    });

    useEffect(() => {
        const loadOnboardingDetails = async () => {
            if (!isCompanyCodeReady) {
                return;
            }

            const locationState = location.state;
            if (locationState?.isSelfRegistration) {
                setOnboardingInfo(prevInfo => ({
                    ...prevInfo,
                    isSelfRegistration: true,
                    profileData: locationState.profileData,
                    companyCode: locationState.companyCode,
                    userDetails: {
                        email: locationState.profileData.email,
                        mobile_phone_number:
                            locationState.profileData.mobilePhoneNumber,
                        first_name: locationState.profileData.firstName,
                        last_name: locationState.profileData.lastName,
                    },
                }));
                setOnboardingStage('eula');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            try {
                const details = await getOnboardingDetails(
                    onboardingCode,
                    authData.companyCode,
                );

                if (details?.onboarding?.length > 0) {
                    const detail = details.onboarding[0];
                    setOnboardingInfo(prevInfo => ({
                        ...prevInfo,
                        userDetails: detail,
                        companyCode: authData.companyCode,
                    }));
                    setOnboardingStage('eula');
                } else {
                    setOnboardingError('Invalid onboarding code.');
                    setOnboardingStage('error');
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    setOnboardingError('Invalid onboarding code.');
                } else {
                    setOnboardingError(
                        'An error occurred while loading onboarding details.',
                    );
                }
                setOnboardingStage('error');
            } finally {
                setIsLoading(false);
            }
        };

        loadOnboardingDetails();
    }, [
        isCompanyCodeReady,
        onboardingCode,
        authData.companyCode,
        companyConfig,
        location.state,
    ]);

    const onEULAAgree = () => {
        setOnboardingStage('set-communication-preferences');
    };

    const onSetCommunicationPreferences = async data => {
        setOnboardingInfo(prevInfo => ({
            ...prevInfo,
            communicationPreferences: {
                email: data.email,
                mobilePhoneNumber: data.mobilePhoneNumber,
            },
        }));

        setOnboardingStage('set-notification-preferences');
    };

    const onSetNotificationPreferences = data => {
        setNotificationPreferences(data);
        setOnboardingStage('set-credentials');
    };

    const onSetCredentials = data => {
        setOnboardingInfo(prevInfo => ({
            ...prevInfo,
            userCredentials: {
                username: data.username,
                password: data.password,
            },
        }));
        Toast.success(
            getText('msg_41cca4f55d6c'),
            {},
            getText('msg_7ce01f638146'),
        );
        navigate(ROUTE_BUILDERS.loginWithCompanyCode(authData.companyCode));
    };

    const background = () => css`
        ${themeStyles?.loginBackground?.type === 'color'
            ? `background: ${themeStyles?.loginBackground?.color}`
            : `background-image: url(${themeStyles?.loginBackground?.image})`}
    `;

    const handleBack = () => {
        switch (onboardingStage) {
            case 'set-communication-preferences':
                setOnboardingStage('eula');
                break;
            case 'set-notification-preferences':
                setOnboardingStage('set-communication-preferences');
                break;
            case 'set-credentials':
                setOnboardingStage('set-notification-preferences');
                break;
            default:
                break;
        }
    };

    const getBackButtonText = () => {
        switch (onboardingStage) {
            case 'set-notification-preferences':
                return 'Back to Contact Information';
            case 'set-credentials':
                return 'Back to Communication Preferences';
            default:
                return '';
        }
    };

    const renderContent = () => {
        if (onboardingStage === 'error') {
            return (
                <div className="text-center">
                    <h2>{onboardingError}</h2>
                    <div className="mt-4 col-12">
                        {getText('msg_6a08c970afcc')}
                        <Link
                            to={ROUTE_BUILDERS.loginWithCompanyCode(
                                authData.companyCode,
                            )}
                            className="btn-link btn me-3"
                        >
                            {getText('msg_79df4603b29b')}
                        </Link>
                        <Link
                            to={ROUTE_BUILDERS.onboardingFindUserWithCompanyCode(
                                authData.companyCode,
                            )}
                            className="btn-link btn col-12"
                        >
                            {getText('msg_7d4eaaef18ed')}
                        </Link>
                    </div>
                </div>
            );
        }

        switch (onboardingStage) {
            case 'eula':
                return (
                    <EULAPage
                        companyConfig={companyConfig}
                        onEULAAgree={onEULAAgree}
                        onboardingInfo={onboardingInfo}
                    />
                );
            case 'set-communication-preferences':
                return (
                    <SetCommunicationPreferencesForm
                        onSetCommunicationPreferences={
                            onSetCommunicationPreferences
                        }
                        onboardingInfo={onboardingInfo}
                    />
                );
            case 'set-notification-preferences':
                return (
                    <SetNotificationPreferencesForm
                        onSetNotificationPreferences={
                            onSetNotificationPreferences
                        }
                        communicationPreferences={
                            onboardingInfo.communicationPreferences
                        }
                        onboardingInfo={onboardingInfo}
                        notificationPreferences={notificationPreferences}
                    />
                );
            case 'set-credentials':
                return (
                    <SetUserCredentialsForm
                        onSetCredentials={onSetCredentials}
                        onboardingInfo={onboardingInfo}
                        notificationPreferences={
                            notificationPreferences.communicationPreferences
                        }
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={`${styles.container}`} css={background}>
            <HeightScaledContent baseHeight={1000}>
                <div
                    className={`${styles.onboardingBox} col-12 col-sm-12 col-md-7 col-lg-5 m-auto`}
                >
                    <div
                        className={`${styles.errorContainer} ${
                            onboardingError ? '' : 'invisible'
                        }`}
                    ></div>

                    {!isCompanyCodeReady || isLoading ? (
                        <Spinner width={50} className={`m-auto`} />
                    ) : (
                        <>
                            <div
                                className={`${styles.authContainer} card modal-content py-5`}
                            >
                                {getBackButtonText() && (
                                    <small
                                        className={`${styles.returnToLoginButton} position-absolute`}
                                        onClick={handleBack}
                                        style={{
                                            display:
                                                onboardingStage === 'eula'
                                                    ? 'none'
                                                    : 'inline-block',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <i className="bi bi-chevron-left ms-3" />{' '}
                                        {getBackButtonText()}
                                    </small>
                                )}

                                <div
                                    className={`form-group text-center my-3 ${styles.logoContainer}`}
                                >
                                    {themeStyles?.primaryLogo ? (
                                        <img
                                            src={themeStyles.primaryLogo}
                                            alt={getText('msg_5807dd602664')}
                                            height="100"
                                        />
                                    ) : (
                                        <PlaceholderLogo height="100" />
                                    )}
                                </div>
                                {renderContent()}
                            </div>
                        </>
                    )}
                </div>
            </HeightScaledContent>
        </div>
    );
};

export default OnboardingWizard;
